import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuoteCart, type CartItem } from "../contexts/QuoteCartContext.js";
import { createQuoteApi } from "../lib/api.js";
import { formatCzk } from "../lib/format.js";
import { CloseIcon, CartIcon, ImageOffIcon, AlertIcon } from "../lib/icons.js";
import { Button } from "./Button.js";
import { EmptyState } from "./EmptyState.js";

/**
 * Pravý slide-in drawer s položkami košíku + formulář pro uložení nabídky.
 */
export function CartDrawer(): JSX.Element | null {
  const {
    drawerOpen,
    closeDrawer,
    items,
    removeItem,
    updateQty,
    clear,
    itemCount,
  } = useQuoteCart();

  // ESC zavírá
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") closeDrawer();
    }
    if (drawerOpen) {
      window.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
      return () => {
        window.removeEventListener("keydown", onKey);
        document.body.style.overflow = "";
      };
    }
    return undefined;
  }, [drawerOpen, closeDrawer]);

  if (!drawerOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label="Nabídka"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 animate-fade-in"
        onClick={closeDrawer}
      />

      {/* Drawer */}
      <aside
        className="relative w-full max-w-md bg-white shadow-2xl flex flex-col animate-fade-in"
        style={{ animationDuration: "200ms" }}
      >
        <header className="h-14 px-5 flex items-center justify-between border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <CartIcon size={18} className="text-zinc-500" />
            <h2 className="font-semibold">
              Nabídka{" "}
              <span className="text-sm text-zinc-400 font-normal">
                · {itemCount} položek
              </span>
            </h2>
          </div>
          <button
            type="button"
            onClick={closeDrawer}
            className="w-8 h-8 rounded-md hover:bg-zinc-100 flex items-center justify-center"
            aria-label="Zavřít"
          >
            <CloseIcon size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-auto">
          {items.length === 0 ? (
            <EmptyState
              icon={<CartIcon size={24} />}
              title="Nabídka je prázdná"
              description="Přidejte díly na stránce detailu nebo přímo z výkresu."
            />
          ) : (
            <ul className="divide-y divide-zinc-100">
              {items.map((item, idx) => (
                <CartRow
                  key={`${item.articleNumber}-${idx}`}
                  item={item}
                  onQty={(q) => updateQty(idx, q)}
                  onRemove={() => removeItem(idx)}
                />
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && <Totals items={items} />}

        {items.length > 0 && (
          <CartActions onClear={clear} onAfterCreate={closeDrawer} />
        )}
      </aside>
    </div>
  );
}

/* ───────────── Row ───────────── */

function CartRow({
  item,
  onQty,
  onRemove,
}: {
  item: CartItem;
  onQty: (q: number) => void;
  onRemove: () => void;
}): JSX.Element {
  const unitNet = parseFloat(item.unitPriceNetCzk);
  const total = unitNet * item.quantity;

  return (
    <li className="p-4 flex gap-3">
      <div className="w-14 h-14 bg-zinc-50 rounded-md flex items-center justify-center overflow-hidden flex-shrink-0 border border-zinc-100">
        {item.subgroupImageUrl ? (
          <img
            src={item.subgroupImageUrl}
            alt=""
            className="max-w-full max-h-full object-contain p-1"
          />
        ) : (
          <ImageOffIcon size={20} className="text-zinc-300" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-zinc-900 leading-snug line-clamp-2">
              {item.articleName ?? "—"}
            </div>
            <div className="mt-0.5 text-xs text-zinc-500 font-mono">
              {item.articleNumber}
              {item.lineNumber != null && (
                <span className="text-zinc-400">  ·  poz. {item.lineNumber}</span>
              )}
            </div>
            {(item.modelLabel || item.subgroupLabel) && (
              <div className="mt-1 text-[11px] text-zinc-400 truncate">
                {[item.modelLabel, item.subgroupLabel]
                  .filter(Boolean)
                  .join("  ·  ")}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onRemove}
            className="text-zinc-400 hover:text-red-500 transition-colors p-1 -m-1"
            aria-label="Odebrat"
          >
            <CloseIcon size={14} />
          </button>
        </div>

        <div className="mt-2 flex items-center justify-between">
          <QtyStepper qty={item.quantity} onChange={onQty} />
          <div className="text-right">
            <div className="text-sm font-semibold">{formatCzk(total)}</div>
            <div className="text-[11px] text-zinc-400">
              {formatCzk(unitNet)} / ks
            </div>
          </div>
        </div>
      </div>
    </li>
  );
}

function QtyStepper({
  qty,
  onChange,
}: {
  qty: number;
  onChange: (q: number) => void;
}): JSX.Element {
  return (
    <div className="inline-flex items-center rounded-md border border-zinc-200 overflow-hidden">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, qty - 1))}
        className="w-7 h-7 flex items-center justify-center text-zinc-500 hover:bg-zinc-100 disabled:opacity-30"
        disabled={qty <= 1}
        aria-label="Snížit"
      >
        −
      </button>
      <input
        type="number"
        value={qty}
        onChange={(e) => {
          const v = parseInt(e.target.value, 10);
          if (Number.isFinite(v)) onChange(v);
        }}
        className="w-10 h-7 text-center text-sm tabular-nums border-0 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        min={1}
        max={9999}
      />
      <button
        type="button"
        onClick={() => onChange(qty + 1)}
        className="w-7 h-7 flex items-center justify-center text-zinc-500 hover:bg-zinc-100"
        aria-label="Zvýšit"
      >
        +
      </button>
    </div>
  );
}

/* ───────────── Totals ───────────── */

function Totals({ items }: { items: CartItem[] }): JSX.Element {
  let net = 0;
  let vat = 0;
  for (const it of items) {
    const u = parseFloat(it.unitPriceNetCzk);
    const n = u * it.quantity;
    net += n;
    vat += n * (it.vatRate / 100);
  }
  const gross = net + vat;
  return (
    <div className="border-t border-zinc-100 px-5 py-3 space-y-1 bg-zinc-50">
      <Row label="Mezisoučet" value={formatCzk(net)} />
      <Row label="DPH 21 %" value={formatCzk(vat)} />
      <div className="flex items-center justify-between pt-2 mt-1 border-t border-zinc-200">
        <span className="text-sm font-semibold">Celkem s DPH</span>
        <span className="text-lg font-semibold text-ktm-orange">
          {formatCzk(gross)}
        </span>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-zinc-500">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

/* ───────────── Actions (create quote) ───────────── */

function CartActions({
  onClear,
  onAfterCreate,
}: {
  onClear: () => void;
  onAfterCreate: () => void;
}): JSX.Element {
  const { items, clear } = useQuoteCart();
  const navigate = useNavigate();
  const [customerName, setCustomerName] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCreate(): Promise<void> {
    if (items.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const { id } = await createQuoteApi({
        customerName: customerName.trim() || undefined,
        note: note.trim() || undefined,
        items: items.map((it) => ({
          articleNumber: it.articleNumber,
          articleName: it.articleName,
          quantity: it.quantity,
          unitPriceNetCzk: it.unitPriceNetCzk,
          vatRate: it.vatRate,
          modelId: it.modelId ?? null,
          componentSubgroupId: it.subgroupId ?? null,
          lineNumber: it.lineNumber ?? null,
        })),
      });
      clear();
      onAfterCreate();
      navigate(`/quote/${id}`);
    } catch (err) {
      setError(String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="border-t border-zinc-100 p-4 space-y-3 bg-white">
      <input
        value={customerName}
        onChange={(e) => setCustomerName(e.target.value)}
        placeholder="Zákazník (volitelné)"
        className="w-full h-9 px-3 rounded-md border border-zinc-200 text-sm placeholder:text-zinc-400 focus:border-ktm-orange focus:ring-2 focus:ring-ktm-orange-100 focus:outline-none"
      />
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Poznámka k nabídce (volitelné)"
        rows={2}
        className="w-full px-3 py-2 rounded-md border border-zinc-200 text-sm placeholder:text-zinc-400 focus:border-ktm-orange focus:ring-2 focus:ring-ktm-orange-100 focus:outline-none resize-none"
      />
      {error && (
        <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1.5">
          <AlertIcon size={14} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      <div className="flex gap-2">
        <Button
          variant="secondary"
          size="md"
          onClick={onClear}
          disabled={submitting}
        >
          Vyprázdnit
        </Button>
        <Button
          onClick={handleCreate}
          fullWidth
          disabled={submitting || items.length === 0}
        >
          {submitting ? "Ukládám…" : "Uložit a vytvořit PDF"}
        </Button>
      </div>
    </div>
  );
}
